/**
 * The advisory boxes in the Archive modal — everything the recruiter should
 * know BEFORE confirming, since confirming archives in Lever irreversibly.
 *
 * Two families, both about the email rather than the archive itself:
 *  - the stage has no feedback call configured, so the rejection email links
 *    the recruiter sheet's general booking page (amber) or nothing at all (red)
 *  - Gmail is not connected, so no draft can be created anywhere
 */
import { Link } from 'react-router-dom';
import { Button } from '@finn-auto/design-system/atoms/button';
import type { EmailDraft } from '../../types/taActionBoard';
import { contentFor } from './emailContentRules';
import { COLORS } from './format';
import type { GmailAuth } from './useGmailToken';

/** Amber and red share a shape; only the palette and the wording differ. */
function WarningBox({
  tone,
  title,
  children,
}: {
  tone: 'amber' | 'red';
  title: string;
  children: React.ReactNode;
}) {
  const palette =
    tone === 'red'
      ? { backgroundColor: COLORS.redBg, borderColor: COLORS.redText, color: COLORS.redText }
      : { backgroundColor: COLORS.orangeBg, borderColor: COLORS.orangeText, color: COLORS.orangeText };
  return (
    <div className="rounded-lg border p-3 text-xs" style={palette}>
      <p className="font-semibold">{title}</p>
      {children}
    </div>
  );
}

const InterviewLinksLink = () => (
  <Link to="/interview-links" className="font-semibold underline">
    Interview Links page
  </Link>
);

/**
 * Where the rejection email's 15-min feedback-call link came from.
 *
 * Only the booking-call templates render such a link, so these warnings apply
 * there alone — on a Tech-case email the feedback is written into the body and
 * no booking link is involved.
 */
export function FeedbackCallWarning({ draft }: { draft: EmailDraft }) {
  const rendersBookingLink = contentFor(draft.branch.split('_')[0], draft.branchKey) === 'booking';
  if (!rendersBookingLink) return null;

  if (draft.bookingLinkMissing) {
    return (
      <WarningBox tone="red" title="No feedback call is set up for this stage">
        <p className="mt-1">
          {draft.taEmail} also has no row in the recruiter overview sheet, so the email shows a
          “[booking link]” placeholder instead of a link. Set up the feedback call on the{' '}
          <InterviewLinksLink />.
        </p>
      </WarningBox>
    );
  }
  if (draft.bookingLinkSource === 'sheet') {
    return (
      <WarningBox tone="amber" title="No feedback call is set up for this stage">
        <p className="mt-1">
          Nothing is configured on the <InterviewLinksLink />, so the email links {draft.taEmail}
          &apos;s general booking page from the recruiter overview sheet instead.
        </p>
      </WarningBox>
    );
  }
  return null;
}

/**
 * The rejection email is created as a draft in the recruiter's OWN Gmail — the
 * only delivery path there is. Without a connection, confirming saves the text
 * to Lever and the candidate hears nothing, so say it before they confirm and
 * offer the connect right here.
 */
export function GmailNotConnectedWarning({ gmail }: { gmail: GmailAuth }) {
  if (gmail.connected) return null;
  return (
    <WarningBox tone="amber" title="Gmail is not connected — no draft will be created">
      <p className="mt-1">
        This email is created as a draft in your own Gmail, so without a connection archiving saves
        the text to Lever and nothing else — the candidate hears nothing until you send it yourself.
      </p>
      <Button
        size="sm"
        variant="outline"
        className="mt-2"
        loading={gmail.connecting}
        onClick={() => void gmail.connect()}
      >
        Connect Gmail
      </Button>
      {gmail.error ? <p className="mt-1">{gmail.error}</p> : null}
    </WarningBox>
  );
}
